/**
 * Secure residence sub-flow (Figma-style frames 26, 27, 29, 32, 35, 38).
 * Checklist item IDs are stored per panel via settlement frame data;
 * each panel maps to one macro checklist ID on IAD step iad-2 for phase overview sync.
 */

export type SecureResidenceFrameId =
  | 'frame-26'
  | 'frame-27'
  | 'frame-29'
  | 'frame-32'
  | 'frame-35'
  | 'frame-38';

export interface ResidenceChecklistDef {
  id: string;
  title: string;
  description?: string;
}

export interface ResidencePanelDefinition {
  frameId: SecureResidenceFrameId;
  path: string;
  progressIndex: number;
  title: string;
  intro: string;
  checklist: ResidenceChecklistDef[];
  /** Matches settlementFlowConfig iad-2 checklist item id */
  macroChecklistItemId: string;
}

export const RESIDENCE_SECURE_PANELS: ResidencePanelDefinition[] = [
  {
    frameId: 'frame-26',
    path: '/frame-26',
    progressIndex: 0,
    title: 'Secure entry points',
    intro:
      'Lock down physical access to the home. Document who has keys and confirm every exterior door and window is secured.',
    macroChecklistItemId: 'iad-2-sr-26',
    checklist: [
      {
        id: 'sr26-1',
        title: 'Walk the perimeter',
        description: 'Check doors, windows, garage, and outbuildings for damage or forced entry.',
      },
      {
        id: 'sr26-2',
        title: 'Re-key or change codes',
        description: 'If legally permitted, change locks or smart-lock codes; collect hidden spare keys.',
      },
      {
        id: 'sr26-3',
        title: 'Log key holders',
        description: 'Record neighbors, cleaners, or relatives who still have access and retrieve keys where possible.',
      },
    ],
  },
  {
    frameId: 'frame-27',
    path: '/frame-27',
    progressIndex: 1,
    title: 'Utilities management',
    intro:
      'Prevent damage and unnecessary charges by locating utilities and deciding what to pause, transfer, or leave active.',
    macroChecklistItemId: 'iad-2-sr-27',
    checklist: [
      {
        id: 'sr27-1',
        title: 'Locate main shutoffs',
        description: 'Water, gas, and electrical mains; label them for other executors or contractors.',
      },
      {
        id: 'sr27-2',
        title: 'Review seasonal systems',
        description: 'HVAC, irrigation, pool equipment—set safe modes or schedule service if the home will sit vacant.',
      },
      {
        id: 'sr27-3',
        title: 'Plan account changes',
        description: 'Note which utility accounts should transfer, close, or move to estate billing.',
      },
    ],
  },
  {
    frameId: 'frame-29',
    path: '/frame-29',
    progressIndex: 2,
    title: 'Mail forwarding',
    intro:
      'Redirect postal mail so bills, legal notices, and tax documents reach the executor instead of piling up.',
    macroChecklistItemId: 'iad-2-sr-29',
    checklist: [
      {
        id: 'sr29-1',
        title: 'Submit USPS change of address',
        description: 'Use official forwarding for the deceased or to a trusted executor address.',
      },
      {
        id: 'sr29-2',
        title: 'Empty the mailbox',
        description: 'Retrieve current mail and secure sensitive items inside the residence.',
      },
      {
        id: 'sr29-3',
        title: 'Update senders you know',
        description: 'Banks, insurers, subscriptions—request electronic delivery where possible.',
      },
    ],
  },
  {
    frameId: 'frame-32',
    path: '/frame-32',
    progressIndex: 3,
    title: 'Property insurance',
    intro:
      'Confirm the home remains covered and that the insurer knows occupancy may have changed.',
    macroChecklistItemId: 'iad-2-sr-32',
    checklist: [
      {
        id: 'sr32-1',
        title: 'Locate the homeowner policy',
        description: 'Declarations page, agent contact, and policy number.',
      },
      {
        id: 'sr32-2',
        title: 'Notify the carrier',
        description: 'Report the death and ask about vacancy, unoccupied home, or executor coverage requirements.',
      },
      {
        id: 'sr32-3',
        title: 'Document coverage decisions',
        description: 'Note premium due dates and any required inspections or photo documentation.',
      },
    ],
  },
  {
    frameId: 'frame-35',
    path: '/frame-35',
    progressIndex: 4,
    title: 'Security system',
    intro:
      'Transfer or disable alarms, cameras, and smart devices so the property stays monitored appropriately.',
    macroChecklistItemId: 'iad-2-sr-35',
    checklist: [
      {
        id: 'sr35-1',
        title: 'Identify the provider and login',
        description: 'Locate panels, apps, or contracts for alarm and camera systems.',
      },
      {
        id: 'sr35-2',
        title: 'Update contacts and call lists',
        description: 'Replace the deceased as primary contact with an executor phone number.',
      },
      {
        id: 'sr35-3',
        title: 'Adjust monitoring for vacancy',
        description: 'Confirm motion alerts, codes, and monitoring level match how the home will be used.',
      },
    ],
  },
  {
    frameId: 'frame-38',
    path: '/frame-38',
    progressIndex: 5,
    title: 'Residence completion checklist',
    intro:
      'Final pass before leaving the home secure: valuables, perishables, pets, and documentation.',
    macroChecklistItemId: 'iad-2-sr-38',
    checklist: [
      {
        id: 'sr38-1',
        title: 'Valuables and firearms',
        description: 'Secure jewelry, cash, firearms, and collectibles per local law and estate instructions.',
      },
      {
        id: 'sr38-2',
        title: 'Perishables and trash',
        description: 'Remove food that will spoil; arrange trash pickup if needed.',
      },
      {
        id: 'sr38-3',
        title: 'Pets and plants',
        description: 'Confirm temporary care for pets; water sensitive plants or document arrangements.',
      },
      {
        id: 'sr38-4',
        title: 'Photo documentation',
        description: 'Capture room-by-room photos or video for the estate file.',
      },
    ],
  },
];

export interface SecureResidenceFramePayload {
  checklist: Record<string, boolean>;
  documents: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    uploadedAt: string;
  }>;
}

export function defaultSecureResidencePayload(): SecureResidenceFramePayload {
  return { checklist: {}, documents: [] };
}

export function normalizeSecureResidenceData(raw: Record<string, unknown> | undefined): SecureResidenceFramePayload {
  const base = defaultSecureResidencePayload();
  if (!raw || typeof raw !== 'object') return base;
  const checklist = raw.checklist;
  const documents = raw.documents;
  return {
    checklist: checklist && typeof checklist === 'object' && !Array.isArray(checklist) ? { ...checklist } : {},
    documents: Array.isArray(documents) ? (documents as SecureResidenceFramePayload['documents']) : [],
  };
}

export function isPanelGranularComplete(panel: ResidencePanelDefinition, data: SecureResidenceFramePayload): boolean {
  return panel.checklist.every((c) => !!data.checklist[c.id]);
}

export function withEstatePath(path: string, estateId: string | null): string {
  return estateId ? `${path}/${estateId}` : path;
}

export function panelByFrameId(id: string): ResidencePanelDefinition | undefined {
  return RESIDENCE_SECURE_PANELS.find((p) => p.frameId === id);
}

export interface ResidencePanelCompletion {
  completedCount: number;
  byFrameId: Record<string, boolean>;
}

/** Load per-panel completion and aggregate count for the secure residence flow. */
export async function loadResidencePanelCompletion(
  estateId: string | null,
  loadFrameData: (frameId: string) => Promise<{ data: Record<string, unknown> } | null>
): Promise<ResidencePanelCompletion> {
  if (!estateId) return { completedCount: 0, byFrameId: {} };
  const byFrameId: Record<string, boolean> = {};
  let completedCount = 0;
  for (const p of RESIDENCE_SECURE_PANELS) {
    const row = await loadFrameData(p.frameId);
    const data = normalizeSecureResidenceData(row?.data);
    const complete = isPanelGranularComplete(p, data);
    byFrameId[p.frameId] = complete;
    if (complete) completedCount++;
  }
  return { completedCount, byFrameId };
}

/** Count how many secure-residence panels have every granular checklist item checked. */
export async function countCompletedResidencePanels(
  estateId: string | null,
  loadFrameData: (frameId: string) => Promise<{ data: Record<string, unknown> } | null>
): Promise<number> {
  const { completedCount } = await loadResidencePanelCompletion(estateId, loadFrameData);
  return completedCount;
}
